import { useLocation } from "react-router-dom";
import { Phone, MessageCircle, Send, CalendarDays } from "lucide-react";
import { useLang } from "@/LanguageContext";

/**
 * Sticky mobile CTA bar fixed to the bottom edge.
 * Visible only on screens < md (Tailwind: hidden on md and above).
 * Hidden on /admin to avoid blocking admin UI.
 */
export default function StickyMobileCTA() {
  const { pathname } = useLocation();
  const { lang } = useLang();

  if (pathname.startsWith("/admin")) return null;

  const scrollToContact = (e) => {
    e.preventDefault();
    if (pathname !== "/") {
      window.location.href = "/#kontakt";
      return;
    }
    document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  const labels = lang === "de"
    ? { phone: "Telefon", whatsapp: "WhatsApp", signal: "Signal", contact: "Termin" }
    : { phone: "Phone", whatsapp: "WhatsApp", signal: "Signal", contact: "Book" };

  return (
    <nav
      data-testid="sticky-mobile-cta"
      aria-label="Mobile contact actions"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0C1620]/95 backdrop-blur-xl border-t border-[#C5993A]/25 shadow-[0_-6px_24px_-8px_rgba(0,0,0,0.7)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul className="grid grid-cols-4">
        <li>
          <a
            href="tel:+4915231495892"
            data-testid="sticky-cta-phone"
            className="flex flex-col items-center justify-center gap-1 py-2.5 text-[#F0F4F8] active:bg-[#C5993A]/20 transition-colors"
          >
            <Phone className="w-5 h-5 text-[#C5993A]" />
            <span className="text-[10px] uppercase tracking-wider">{labels.phone}</span>
          </a>
        </li>
        <li>
          <a
            href="https://wa.me/4915231495892"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-cta-whatsapp"
            className="flex flex-col items-center justify-center gap-1 py-2.5 text-[#F0F4F8] active:bg-[#C5993A]/20 transition-colors border-l border-[#C5993A]/15"
          >
            <MessageCircle className="w-5 h-5 text-[#C5993A]" />
            <span className="text-[10px] uppercase tracking-wider">{labels.whatsapp}</span>
          </a>
        </li>
        <li>
          <a
            href="https://signal.me/#p/+4915231495892"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-cta-signal"
            className="flex flex-col items-center justify-center gap-1 py-2.5 text-[#F0F4F8] active:bg-[#C5993A]/20 transition-colors border-l border-[#C5993A]/15"
          >
            <Send className="w-5 h-5 text-[#C5993A]" />
            <span className="text-[10px] uppercase tracking-wider">{labels.signal}</span>
          </a>
        </li>
        <li>
          <a
            href="#kontakt"
            onClick={scrollToContact}
            data-testid="sticky-cta-termin"
            className="flex flex-col items-center justify-center gap-1 py-2.5 text-white bg-gradient-to-r from-[#C5993A] to-[#8B6B20] active:opacity-80 transition-opacity"
          >
            <CalendarDays className="w-5 h-5 text-white" />
            <span className="text-[10px] uppercase tracking-wider font-medium">{labels.contact}</span>
          </a>
        </li>
      </ul>
    </nav>
  );
}
